import React, { useEffect, useState } from "react";
import {
  Clock, Layers, BadgeCheck, Rocket, Umbrella,
  Truck, Calculator, Store, Wallet, Zap, Users, ShieldCheck, Sparkles, Utensils,
  SprayCan, Shirt, PawPrint, Pill, Baby, Headphones, Handshake, BrainCircuit,
  Building2, PiggyBank, Check, ArrowRight, Banknote,
} from "lucide-react";

/* ------------------------------------------------------------------ *
 * Primitives — authored at the source deck's native 960 x 540 scale.
 * Every px here equals one point in Verqo_Investor_Pitch.pdf.
 * ------------------------------------------------------------------ */

export const TAGLINE = "WHOLESALE AT THE SPEED OF BUSINESS";
const LIME = "#dcff3a";
const GREEN = "#2e6b57";
const GREEN_2 = "#3e8b70";
const OFFWHITE = "#f5f3ee";
const CORAL = "#e8705f";
const INK = "#0b1914";

export const Lockup = ({ scale = 1 }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 9 * scale }}>
    <div
      style={{
        width: 22 * scale, height: 22 * scale, borderRadius: 6 * scale,
        background: LIME, display: "grid", placeItems: "center", flex: "0 0 auto",
      }}
    >
      <svg viewBox="0 0 32 32" width={15 * scale} height={15 * scale} fill="none">
        <path d="M7 11 L15 23 L27 4" stroke={INK} strokeWidth={5} strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </div>
    <span style={{ fontSize: 17 * scale, fontWeight: 700, letterSpacing: "-0.03em", color: "#fff" }}>verqo</span>
  </div>
);

export const Frame = ({ n, total = 24, prefix = "pitch", eyebrow, title, size = 32, children, footnote, footnoteTop = 470, tagline }) => (
  <div className="vpd-slide" data-testid={`${prefix}-slide-${n}`}>
    <div className="vpd-head">
      <Lockup />
      <div className="vpd-head-right">{tagline || "SEED ROUND · 2026"}</div>
    </div>
    {eyebrow && (
      <div className="vpd-eyebrow an" style={{ "--d": "40ms" }}>
        <i />
        {eyebrow}
      </div>
    )}
    {title && (
      <h2 className="vpd-title h an" style={{ fontSize: size, "--d": "110ms" }} data-testid={`${prefix}-title-${n}`}>
        {title}
      </h2>
    )}
    {children}
    {footnote && (
      <div className="vpd-footnote an" style={{ top: footnoteTop, "--d": "640ms" }}>{footnote}</div>
    )}
    <div className="vpd-foot">
      <span data-testid={`${prefix}-page-${n}`}>{String(n).padStart(2, "0")} / {total}</span>
      <span>CONFIDENTIAL</span>
    </div>
  </div>
);

export const Body = ({ top, children, style, cls = "" }) => (
  <div className={`vpd-body ${cls}`} style={{ top, ...style }}>{children}</div>
);

export const Mono = ({ children, size = 9, color = "#8ea096", ls = "0.14em", style }) => (
  <div className="m" style={{ fontSize: size, color, letterSpacing: ls, ...style }}>{children}</div>
);

/* count-up for the headline figures */
export const Count = ({ to, dur = 1150, delay = 240, fmt = (v) => Math.round(v).toLocaleString() }) => {
  const [v, setV] = useState(0);
  useEffect(() => {
    if (typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setV(to);
      return;
    }
    let raf = 0;
    let t0 = 0;
    const tm = setTimeout(() => {
      const step = (ts) => {
        if (!t0) t0 = ts;
        const p = Math.min(1, (ts - t0) / dur);
        setV(to * (1 - Math.pow(1 - p, 3)));
        if (p < 1) raf = requestAnimationFrame(step);
      };
      raf = requestAnimationFrame(step);
    }, delay);
    return () => { clearTimeout(tm); cancelAnimationFrame(raf); };
  }, [to, dur, delay]);
  return <>{fmt(v)}</>;
};

/* donut built from stroked arcs */
export const Donut = ({ cx, cy, r, w, data, delay = 220 }) => {
  const C = 2 * Math.PI * r;
  let acc = 0;
  const total = data.reduce((s, d) => s + d.v, 0);
  return (
    <g className="spin-in" style={{ "--d": `${delay}ms`, transformOrigin: `${cx}px ${cy}px` }}>
      {data.map((d, i) => {
        const frac = d.v / total;
        const len = C * frac;
        const off = -C * acc;
        acc += frac;
        return (
          <circle
            key={i}
            cx={cx} cy={cy} r={r}
            fill="none"
            stroke={d.c}
            strokeWidth={w}
            strokeDasharray={`${len - 1.2} ${C - len + 1.2}`}
            strokeDashoffset={off}
            transform={`rotate(-90 ${cx} ${cy})`}
          />
        );
      })}
    </g>
  );
};

export const Dot = ({ c = LIME, s = 10 }) => (
  <span style={{ width: s, height: s, borderRadius: 999, background: c, display: "block", flex: "0 0 auto" }} />
);

export const Row = ({ children, style, className = "" }) => (
  <div className={className} style={{ display: "flex", alignItems: "center", ...style }}>{children}</div>
);

/* ------------------------------------------------------------------ *
 * 01 — Cover
 * ------------------------------------------------------------------ */
const S1 = () => (
  <Frame n={1} tagline={TAGLINE}>
    {/* concentric rings */}
    <div style={{ position: "absolute", left: 512, top: 48, width: 448, height: 448 }}>
      {[224, 168, 112].map((r, i) => (
        <div
          key={r}
          className="an-s"
          style={{
            position: "absolute", left: 224 - r, top: 224 - r, width: r * 2, height: r * 2,
            borderRadius: 999, background: ["#101d18", "#13231d", "#172820"][i],
            "--d": `${180 + i * 110}ms`,
          }}
        />
      ))}
      <div
        className="an-s"
        style={{
          position: "absolute", left: 170, top: 170, width: 108, height: 108, borderRadius: 26,
          background: LIME, display: "grid", placeItems: "center",
          boxShadow: "0 24px 60px rgba(220,255,58,.18)", "--d": "520ms",
        }}
      >
        <svg viewBox="0 0 32 32" width={74} height={74} fill="none">
          <path d="M7 11 L15 23 L27 4" stroke={INK} strokeWidth={5.2} strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>
    </div>

    <div style={{ position: "absolute", left: 43.2, top: 132, width: 500 }}>
      <div className="m an" style={{ fontSize: 10, letterSpacing: "0.16em", color: LIME, "--d": "80ms" }}>
        WHOLESALE BANKING FOR BUYERS &amp; SELLERS
      </div>
      <h1 className="h" style={{ fontSize: 56, lineHeight: "63.8px", position: "absolute", top: 22, left: 0 }}>
        <span className="an" style={{ display: "block", "--d": "160ms" }}>The operating</span>
        <span className="an" style={{ display: "block", "--d": "250ms" }}>system for</span>
        <span className="an lime" style={{ display: "block", "--d": "340ms" }}>wholesale.</span>
      </h1>
      <div className="serif an" style={{ fontSize: 30, position: "absolute", top: 215, color: "#e8efe9", "--d": "440ms" }}>
        Wholesale, instant.
      </div>
      <div className="an" style={{ width: 82, height: 3, background: LIME, position: "absolute", top: 268, "--d": "520ms" }} />
      <div className="an" style={{ fontSize: 14, position: "absolute", top: 288, whiteSpace: "nowrap", "--d": "580ms" }}>
        <b>Raising $5M seed</b>
        <span className="grey">{"  ·  $2.5M equity + $2.5M reserve facility"}</span>
      </div>
      <div className="m an" style={{ fontSize: 9, position: "absolute", top: 320, color: "#6f8178", whiteSpace: "nowrap", "--d": "640ms" }}>
        ZEV OSTREICHER · FOUNDER &amp; CEO · VERQO.COM
      </div>
    </div>
  </Frame>
);

/* ------------------------------------------------------------------ *
 * 02 — Verqo in 30 seconds
 * ------------------------------------------------------------------ */
const S2_CARDS = [
  { ic: Clock, k: "PROBLEM", v: "45–60", d: "days to get paid · 5–6 tools" },
  { ic: Layers, k: "PRODUCT", v: "1 system", d: "orders · terms · payout · freight · books" },
  { ic: BadgeCheck, k: "PROOF", v: "$40K/mo", d: "live on Verqo · 9 partners committed" },
  { ic: Rocket, k: "PLAN", v: "$524M/mo", d: "GMV by month 24 · EBITDA+ Jun ’27" },
];

const S2 = () => (
  <Frame
    n={2}
    eyebrow="VERQO IN 30 SECONDS"
    size={34}
    title={<>Terms for buyers.<span className="lime"> Cash on Day 1 for sellers.</span></>}
  >
    <Body top={197.6} style={{ display: "flex", gap: 17.5 }}>
      {S2_CARDS.map((c, i) => {
        const Ic = c.ic;
        return (
          <div
            key={c.k}
            className="vpd-card an"
            style={{ width: 205.4, height: 229, padding: "28px 21.6px", "--d": `${200 + i * 90}ms` }}
          >
            <Ic size={25} color={LIME} strokeWidth={1.6} />
            <Mono size={9} color={LIME} style={{ marginTop: 20 }}>{c.k}</Mono>
            <div className="h" style={{ fontSize: 31, marginTop: 11 }}>{c.v}</div>
            <div style={{ fontSize: 12, color: "#9fb0a6", marginTop: 20, lineHeight: "16px" }}>{c.d}</div>
          </div>
        );
      })}
    </Body>
  </Frame>
);

/* ------------------------------------------------------------------ *
 * 03 — The problem
 * ------------------------------------------------------------------ */
const S3_NODES = [
  { lg: "bill.png", b: "Bill.com", l: "Invoicing", x: 186, y: 190 },
  { lg: "stripe.png", b: "Stripe", l: "Payments", x: 331, y: 246 },
  { lg: "dnb.png", b: "Dun & Bradstreet", l: "Credit checks", x: 331, y: 357 },
  { lg: "shipstation.png", b: "ShipStation", l: "Shipping", x: 186, y: 413 },
  { lg: "allianz.png", b: "Allianz Trade", l: "Insurance", x: 41, y: 357 },
  { lg: "quickbooks.png", b: "QuickBooks", l: "Accounting", x: 41, y: 246 },
];
const S3_STATS = [
  { v: "45–60 days", d: "to get paid · BRND’s own: 60" },
  { v: "40%", d: "of US B2B invoices paid late" },
  { v: "~2 people", d: "$146K/yr payroll to bill, chase & reconcile · $18M seller" },
];

const S3 = () => (
  <Frame
    n={3}
    eyebrow="THE PROBLEM"
    size={29}
    title={<>Six tools to sell one order.<span className="lime"> Then 45–60 days to get paid.</span></>}
  >
    <div style={{ position: "absolute", left: 43.2, top: 165, width: 460, height: 310 }}>
      <svg width="460" height="310" style={{ position: "absolute", inset: 0 }}>
        {S3_NODES.map((nd, i) => {
          const cx = nd.x + 68.5;
          const cy = nd.y - 165 + 25;
          const t0 = 0.34;
          const t1 = 0.74;
          return (
            <line
              key={i}
              x1={212 + (cx - 212) * t0} y1={163 + (cy - 163) * t0}
              x2={212 + (cx - 212) * t1} y2={163 + (cy - 163) * t1}
              stroke="rgba(220,255,58,.3)" strokeWidth="1.1" strokeDasharray="3 4"
              className="dash" style={{ "--len": 120, "--d": `${320 + i * 60}ms` }}
            />
          );
        })}
      </svg>
      {S3_NODES.map((nd, i) => (
        <div
          key={nd.l}
          className="vpd-chip an"
          style={{
            position: "absolute", left: nd.x, top: nd.y - 165, width: 137, height: 50,
            display: "flex", alignItems: "center", gap: 10, padding: "0 11px", "--d": `${240 + i * 70}ms`,
          }}
        >
          <img
            src={`/pitch-assets/tools/${nd.lg}`}
            alt={nd.b}
            style={{ width: 22, height: 22, borderRadius: 5, flex: "0 0 auto" }}
          />
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 10.5, color: "#e3ece7", lineHeight: "13px" }}>{nd.l}</div>
            <div style={{ fontSize: 8.5, color: "#6f8178", marginTop: 2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{nd.b}</div>
          </div>
        </div>
      ))}
      <div
        className="an-s"
        style={{
          position: "absolute", left: 143.5, top: 138, width: 137, height: 50, borderRadius: 999,
          background: "#13271f", border: `1.4px solid ${LIME}`, display: "grid", placeItems: "center",
          "--d": "180ms",
        }}
      >
        <span className="h" style={{ fontSize: 14 }}>One seller</span>
      </div>
    </div>

    <div style={{ position: "absolute", left: 547.2, top: 178, width: 369.6 }}>
      {S3_STATS.map((s, i) => (
        <div key={s.v} className="an" style={{ "--d": `${300 + i * 110}ms` }}>
          <div className="vpd-rule" />
          <div className="h lime" style={{ fontSize: 40, marginTop: 20 }}>{s.v}</div>
          <div style={{ fontSize: 11, color: "#9fb0a6", margin: "11px 0 20px" }}>{s.d}</div>
        </div>
      ))}
    </div>
  </Frame>
);

/* ------------------------------------------------------------------ *
 * 04 — Why us · the founder
 * ------------------------------------------------------------------ */
const S4_STATS = [
  ["14+", "years"], ["5", "companies"], ["$30M+", "sold"],
  ["$8M", "peak year · all ventures"], ["300+", "supplier relationships"], ["20K", "SKUs managed"],
];
const S4_TL = [
  ["2012", "Greenuvite", "Green Herb supplements on Amazon"],
  ["2016", "Zoganic", "600+ stores · 50+ partners"],
  ["2018", "E-com consultant", "Chefman, Gourmia · 300–400% MoM"],
  ["2022", "BRND Direct", "$7M/yr wholesale"],
  ["2023", "BRND Media", "$1M+ client sales"],
  ["2024", "Verqo", "The rails we needed"],
];

const S4 = () => (
  <Frame
    n={4}
    eyebrow="WHY US · THE FOUNDER"
    title={<>We didn’t study wholesale.<span className="lime"> We lived it.</span></>}
  >
    <Body top={165} style={{ display: "flex" }}>
      {S4_STATS.map(([v, l], i) => (
        <div key={l} className="an" style={{ width: 146, "--d": `${180 + i * 70}ms` }}>
          <div className="h lime" style={{ fontSize: 30 }}>{v}</div>
          <div style={{ fontSize: 11, color: "#9fb0a6", marginTop: 7 }}>{l}</div>
        </div>
      ))}
    </Body>

    <div style={{ position: "absolute", left: 43.2, top: 265, width: 873.6 }}>
      <div
        className="gw"
        style={{
          position: "absolute", left: 21.6, top: 43.5, height: 1,
          background: "rgba(255,255,255,.16)", "--w": "790px", "--d": "380ms",
        }}
      />
      <div style={{ display: "flex" }}>
        {S4_TL.map(([y, name, desc], i) => (
          <div key={y} className="an" style={{ width: 149.3, "--d": `${420 + i * 85}ms` }}>
            <Mono size={10} color={i === 5 ? LIME : "#6f8178"}>{y}</Mono>
            <div
              style={{
                width: 14, height: 14, borderRadius: 999, marginTop: 11,
                background: i === 5 ? LIME : OFFWHITE,
                boxShadow: i === 5 ? `0 0 0 4px rgba(220,255,58,.16)` : "none",
              }}
            />
            <div className="h" style={{ fontSize: 13, marginTop: 17, color: i === 5 ? LIME : "#fff" }}>{name}</div>
            <div style={{ fontSize: 10.5, color: "#8ea096", marginTop: 9, lineHeight: "14px", paddingRight: 12 }}>{desc}</div>
          </div>
        ))}
      </div>
    </div>

    <Row className="an" style={{ position: "absolute", left: 43.2, top: 428, gap: 18, "--d": "900ms" }}>
      <img src="/pitch-assets/brand_zoganic.png" alt="Zoganic" style={{ height: 36, borderRadius: 8 }} />
      <img src="/pitch-assets/brand_brnd.png" alt="BRND Direct" style={{ height: 32 }} />
      <span style={{ width: 1, height: 30, background: "rgba(255,255,255,.14)", marginLeft: 8 }} />
      <Mono size={9} color="#6f8178" style={{ marginLeft: 4 }}>IN THE PRESS</Mono>
      <span style={{ fontSize: 12, fontWeight: 700, color: "#dfe8e2" }}>BevNET · Beverage Industry · Inc.</span>
    </Row>
  </Frame>
);

/* ------------------------------------------------------------------ *
 * 05 — The product
 * ------------------------------------------------------------------ */
const S5_ROWS = [
  { ic: Store, t: "Order", s: "Buyer portal · pay by bank or card", b: "LIVE" },
  { ic: Banknote, t: "Finance", s: "Net 30/60/90 for the buyer", b: "NEXT" },
  { ic: Umbrella, t: "Insure", s: "Cover on every order", b: "NEXT" },
  { ic: Truck, t: "Ship", s: "Parcel, pallet or truckload", b: "NEXT" },
  { ic: Zap, t: "Get paid", s: "Seller paid Day 1", b: "NEXT" },
];

const Badge = ({ kind }) =>
  kind === "LIVE" ? (
    <span className="vpd-pill" style={{ background: LIME, color: INK, fontSize: 9, padding: "5px 11px", fontWeight: 500 }}>LIVE</span>
  ) : (
    <span className="vpd-pill" style={{ border: `1px solid ${LIME}`, color: LIME, fontSize: 9, padding: "4px 11px" }}>NEXT</span>
  );

const S5 = () => (
  <Frame
    n={5}
    eyebrow="THE PRODUCT"
    title={<>One screen.<span className="lime"> Everything is one click.</span></>}
  >
    <Row style={{ position: "absolute", left: 43.2, top: 148, gap: 10 }}>
      {["PAID INSTANTLY", "EVERY BUYER SCORED", "ORDERS TO FREIGHT"].map((t, i) => (
        <span
          key={t}
          className="vpd-pill an"
          style={{
            background: "#0f241e", border: "1px solid rgba(220,255,58,.22)", color: LIME,
            fontSize: 9, padding: "6px 14px", "--d": `${180 + i * 80}ms`,
          }}
        >
          {t}
        </span>
      ))}
    </Row>

    <div className="an-s" style={{ position: "absolute", left: 43.2, top: 184, "--d": "250ms" }}>
      <img
        src="/pitch-assets/app_seller.png"
        alt="Verqo seller dashboard"
        style={{ width: 511.2, height: 279.8, display: "block" }}
      />
    </div>
    <Mono size={8.5} color="#5c6e65" style={{ position: "absolute", left: 43.2, top: 470 }}>
      ILLUSTRATIVE · VERQO.COM/PITCH APP SCREENS
    </Mono>

    <div style={{ position: "absolute", left: 556, top: 184, width: 360.8 }}>
      {S5_ROWS.map((r, i) => {
        const Ic = r.ic;
        return (
          <div
            key={r.t}
            className={`${r.b === "LIVE" ? "vpd-card-a" : "vpd-card"} an`}
            style={{
              height: 48, marginBottom: 9, padding: "0 13px", display: "flex", alignItems: "center",
              gap: 12, "--d": `${300 + i * 85}ms`,
            }}
          >
            <Ic size={21} color={LIME} strokeWidth={1.6} />
            <div style={{ flex: 1 }}>
              <div className="h" style={{ fontSize: 13 }}>{r.t}</div>
              <div style={{ fontSize: 10, color: "#8ea096", marginTop: 3 }}>{r.s}</div>
            </div>
            <Badge kind={r.b} />
          </div>
        );
      })}
      <Row className="an" style={{ gap: 9, marginTop: 7, "--d": "760ms" }}>
        <Badge kind="LIVE" />
        <span style={{ fontSize: 10.5, color: "#9fb0a6" }}>running today</span>
        <span style={{ width: 10 }} />
        <Badge kind="NEXT" />
        <span style={{ fontSize: 10.5, color: "#9fb0a6" }}>with bank + credit partner</span>
      </Row>
    </div>
  </Frame>
);

/* ------------------------------------------------------------------ *
 * 06 — The data moat
 * ------------------------------------------------------------------ */
const S6 = () => {
  const r = 62;
  const C = 2 * Math.PI * r;
  return (
    <Frame
      n={6}
      eyebrow="THE DATA MOAT"
      title={<>Every buyer, scored.<span className="lime"> From live network data.</span></>}
      footnote={<>Every order makes every score sharper: <b className="lime">our data moat.</b></>}
      footnoteTop={476}
    >
      <div className="vpd-card an" style={{ position: "absolute", left: 43.2, top: 172, width: 374.4, height: 294, padding: "22px 25px", "--d": "200ms" }}>
        <Mono size={9}>NEW BUYER · ILLUSTRATIVE</Mono>
        <div className="h" style={{ fontSize: 22, marginTop: 20 }}>Northline Retail Co.</div>
        <div style={{ fontSize: 12, color: "#9fb0a6", marginTop: 11 }}>
          Wants <b style={{ color: "#fff" }}>Net 60 · $240,000</b>
        </div>
        <div className="vpd-rule" style={{ margin: "23px 0 17px" }} />
        <Mono size={9} color={LIME} style={{ opacity: 0.85 }}>WHAT NO CREDIT REPORT SHOWS</Mono>
        <div style={{ marginTop: 16 }}>
          {["Defaulted last week · Supplier A", "Defaulted · Supplier B", "45 days late · Supplier C"].map((t, i) => (
            <Row key={t} className="an" style={{ gap: 12, marginBottom: 17, "--d": `${420 + i * 110}ms` }}>
              <Dot c={CORAL} s={9} />
              <span style={{ fontSize: 11.5, color: "#dfe8e2" }}>{t}</span>
            </Row>
          ))}
        </div>
      </div>

      <div className="an" style={{ position: "absolute", left: 434, top: 312, "--d": "620ms" }}>
        <ArrowRight size={22} color={LIME} strokeWidth={2} />
      </div>

      <div className="vpd-card-a an" style={{ position: "absolute", left: 491.2, top: 172, width: 425.6, height: 294, padding: "22px 24px", "--d": "300ms" }}>
        <Mono size={9} color={LIME}>VERQO TRADE SCORE</Mono>
        <Row style={{ marginTop: 14, gap: 20 }}>
          <svg width={158} height={158} style={{ flex: "0 0 auto" }}>
            <circle cx={79} cy={79} r={r} fill="none" stroke="#17302a" strokeWidth={30} />
            <circle
              cx={79} cy={79} r={r} fill="none" stroke={CORAL} strokeWidth={30}
              strokeDasharray={`${C * 0.28} ${C}`} transform="rotate(-90 79 79)"
              className="arc" style={{ "--len": C * 0.28, "--d": "520ms" }}
            />
            <circle cx={79} cy={79} r={r - 15} fill="#0d2019" />
            <text x={79} y={83} textAnchor="middle" className="h" style={{ fontSize: 34, fill: "#fff" }}>28</text>
            <text x={79} y={99} textAnchor="middle" style={{ fontSize: 9, fill: "#7d8f85", fontFamily: "DM Sans" }}>out of 100</text>
          </svg>
          <div>
            <span className="vpd-pill" style={{ border: `1px solid ${CORAL}`, color: CORAL, fontSize: 9, padding: "5px 12px" }}>HIGH RISK</span>
            <div className="h" style={{ fontSize: 15, marginTop: 17 }}>Terms: prepay only</div>
            <div style={{ fontSize: 11.5, color: "#9fb0a6", marginTop: 10 }}>Scored in seconds, before you ship</div>
          </div>
        </Row>
        <div style={{ marginTop: 18 }}>
          {[["Credit bureau", "Days old · thin file", "#8ea096"], ["Verqo", "Every order on the network", LIME]].map(([a, b, c]) => (
            <Row key={a} style={{ marginBottom: 10 }}>
              <span style={{ fontSize: 11.5, width: 118, color: c, fontWeight: c === LIME ? 700 : 400 }}>{a}</span>
              <span style={{ fontSize: 11.5, color: "#cfdcd4" }}>{b}</span>
            </Row>
          ))}
        </div>
      </div>
    </Frame>
  );
};

/* ------------------------------------------------------------------ *
 * 07 — Banking at the core
 * ------------------------------------------------------------------ */
const FLOW = [
  { ic: Store, l: "Order" }, { ic: Truck, l: "Shipment" }, { ic: Umbrella, l: "Insurance" },
  { ic: Banknote, l: "Financing" }, { ic: Wallet, l: "Buyer payment" },
];

const FlowChips = ({ delay }) => (
  <Row style={{ gap: 11 }}>
    {FLOW.map((f, i) => {
      const Ic = f.ic;
      return (
        <div
          key={f.l}
          className="vpd-chip an"
          style={{ width: 90, height: 70, display: "grid", placeItems: "center", gap: 7, "--d": `${delay + i * 70}ms` }}
        >
          <Ic size={20} color="#c6d4cb" strokeWidth={1.5} />
          <span style={{ fontSize: 10.5, color: "#cfdcd4" }}>{f.l}</span>
        </div>
      );
    })}
  </Row>
);

const S7 = () => (
  <Frame
    n={7}
    eyebrow="BANKING AT THE CORE"
    title={<>Every dollar lands<span className="lime"> already matched.</span></>}
    footnote={<span style={{ color: "#9fb0a6" }}>QuickBooks / NetSuite stay and sync</span>}
    footnoteTop={474}
  >
    <div className="vpd-card an" style={{ position: "absolute", left: 43.2, top: 172, width: 873.6, height: 134, padding: "15px 21.6px", "--d": "180ms" }}>
      <Mono size={9} color="#7d8f85">EVERYONE ELSE · BANK SITS OUTSIDE</Mono>
      <Row style={{ marginTop: 11 }}>
        <FlowChips delay={240} />
        <svg width="54" height="10" style={{ margin: "0 12px" }}>
          <line x1="0" y1="5" x2="46" y2="5" stroke="#6f8178" strokeWidth="1.6" strokeDasharray="5 4" />
          <path d="M46 1 L52 5 L46 9" fill="none" stroke="#6f8178" strokeWidth="1.6" />
        </svg>
        <div style={{ width: 44, height: 44, borderRadius: 999, border: `1.5px solid ${CORAL}`, display: "grid", placeItems: "center", flex: "0 0 auto" }}>
          <span className="h" style={{ fontSize: 20, color: CORAL }}>?</span>
        </div>
        <div style={{ marginLeft: 18 }}>
          <div className="h" style={{ fontSize: 15 }}>Bank statement</div>
          <div style={{ fontSize: 11, color: CORAL, marginTop: 7 }}>Matched by hand · weeks late</div>
        </div>
      </Row>
    </div>

    <div className="vpd-card-a an" style={{ position: "absolute", left: 43.2, top: 322, width: 873.6, height: 136, padding: "15px 21.6px", "--d": "320ms" }}>
      <Row style={{ justifyContent: "space-between" }}>
        <Mono size={9} color={LIME}>VERQO · THE BANK IS THE CORE</Mono>
        <Badge kind="NEXT" />
      </Row>
      <Row style={{ marginTop: 11 }}>
        <FlowChips delay={400} />
        <svg width="78" height="10" style={{ margin: "0 10px" }}>
          <line x1="0" y1="5" x2="70" y2="5" stroke={LIME} strokeWidth="1.8" className="dash" style={{ "--len": 70, "--d": "700ms" }} />
          <path d="M70 1 L76 5 L70 9" fill="none" stroke={LIME} strokeWidth="1.8" />
        </svg>
        <div
          className="an-s"
          style={{
            width: 242, height: 76, borderRadius: 12, background: LIME, color: INK,
            display: "grid", placeItems: "center", gap: 6, "--d": "760ms", flex: "0 0 auto",
          }}
        >
          <span className="h" style={{ fontSize: 16 }}>Verqo ledger</span>
          <span style={{ fontSize: 10.5, opacity: 0.82 }}>Reconciled the moment money moves</span>
        </div>
      </Row>
    </div>
  </Frame>
);

/* ------------------------------------------------------------------ *
 * 08 — Why customers switch
 * ------------------------------------------------------------------ */
const SWITCH = [
  { ic: Clock, l: "Get paid", a: "Day 45–60", b: "Day 1" },
  { ic: Layers, l: "Tools", a: "5–6", b: "1" },
  { ic: Users, l: "Staff to bill & chase", a: "~2", b: "~0.5" },
  { ic: ShieldCheck, l: "Buyer default risk", a: "Yours", b: "Not yours" },
];

const S8 = () => (
  <Frame
    n={8}
    eyebrow="WHY CUSTOMERS SWITCH"
    title={<>Paid on Day 1.<span className="lime"> Fewer people. One system.</span></>}
    footnote={<><b style={{ color: "#fff" }}>$6–7 saved per $1 paid to Verqo</b>{"  ·  pays back in under a week"}</>}
    footnoteTop={452}
  >
    {[0, 1].map((col) => (
      <div
        key={col}
        className={`${col ? "vpd-card-a" : "vpd-card"} an`}
        style={{
          position: "absolute", left: col ? 491.2 : 43.2, top: 172, width: 425.6, height: 260,
          padding: "20px 24px", "--d": `${col ? 300 : 200}ms`,
        }}
      >
        <Mono size={9} color={col ? LIME : "#7d8f85"}>{col ? "ON VERQO" : "TODAY"}</Mono>
        <div style={{ marginTop: 14 }}>
          {SWITCH.map((s, i) => {
            const Ic = s.ic;
            return (
              <Row key={s.l} className="an" style={{ height: 48, gap: 12, "--d": `${(col ? 380 : 280) + i * 80}ms` }}>
                <Ic size={19} color={col ? LIME : "#7d8f85"} strokeWidth={1.6} />
                <span style={{ fontSize: 12.5, color: "#dfe8e2", flex: 1 }}>{s.l}</span>
                <span className="h" style={{ fontSize: 17, color: col ? LIME : "#92a49a" }}>{col ? s.b : s.a}</span>
              </Row>
            );
          })}
        </div>
      </div>
    ))}
    <div className="an" style={{ position: "absolute", left: 463, top: 294, "--d": "640ms" }}>
      <ArrowRight size={18} color={LIME} strokeWidth={2} />
    </div>
  </Frame>
);

/* ------------------------------------------------------------------ *
 * 09 — What a customer saves
 * ------------------------------------------------------------------ */
const SAVES = [
  { t: "Starter", s: "$2.4M sales", v: 115, lbl: "$115K", pct: 22, staff: "0.5", cash: "$0.28M", c: GREEN },
  { t: "Growth", s: "$18M sales", v: 538, lbl: "$538K", pct: 28, staff: "1.4", cash: "$2.1M", c: LIME },
  { t: "Pro", s: "$45M sales", v: 1180, lbl: "$1.18M", pct: 38, staff: "2.5", cash: "$5.3M", c: GREEN },
  { t: "Enterprise", s: "$90M sales", v: 2090, lbl: "$2.09M", pct: 44, staff: "2.8", cash: "$10.6M", c: GREEN },
];

const S9 = () => (
  <Frame
    n={9}
    eyebrow="WHAT A CUSTOMER SAVES"
    title={<>An $18M wholesaler saves<span className="lime"> $538K a year.</span></>}
    footnote={<Mono size={8} color="#5c6e65">PER YEAR, NET OF EVERYTHING PAID TO VERQO · PUBLIC LIST PRICES + BLS SALARIES, OCT 2026</Mono>}
    footnoteTop={476}
  >
    <Row className="an" style={{ position: "absolute", left: 547, top: 174, width: 370, "--d": "200ms" }}>
      {["% OF COST", "STAFF FREED", "CASH FREED"].map((h) => (
        <Mono key={h} size={9} color="#7d8f85" style={{ width: 123, textAlign: "center" }}>{h}</Mono>
      ))}
    </Row>

    <div style={{ position: "absolute", left: 43.2, top: 200 }}>
      {SAVES.map((r, i) => (
        <Row key={r.t} className="an" style={{ height: 66.4, "--d": `${260 + i * 100}ms` }}>
          <div style={{ width: 154 }}>
            <div className="h" style={{ fontSize: 15 }}>{r.t}</div>
            <div style={{ fontSize: 10.5, color: "#8ea096", marginTop: 6 }}>{r.s}</div>
          </div>
          <div style={{ width: 303.2, height: 32, borderRadius: 7, background: "#122a22", position: "relative", overflow: "hidden" }}>
            <div
              className="gw"
              style={{
                position: "absolute", left: 0, top: 0, height: 32, borderRadius: 7, background: r.c,
                "--w": `${Math.max(9, (r.v / 2090) * 100)}%`, "--d": `${420 + i * 100}ms`,
              }}
            />
            <span
              className="h an"
              style={
                r.v > 1800
                  ? { position: "absolute", top: 8, right: 14, fontSize: 14, color: INK, "--d": `${700 + i * 100}ms` }
                  : { position: "absolute", top: 8, left: `calc(${Math.max(9, (r.v / 2090) * 100)}% + 12px)`, fontSize: 14, color: "#fff", "--d": `${700 + i * 100}ms` }
              }
            >
              {r.lbl}
            </span>
          </div>
          <Row style={{ width: 370, marginLeft: 44 }}>
            <div className="h lime" style={{ width: 123, textAlign: "center", fontSize: 16 }}>
              <Count to={r.pct} delay={620 + i * 100} fmt={(v) => `${Math.round(v)}%`} />
            </div>
            <div className="h" style={{ width: 123, textAlign: "center", fontSize: 16 }}>{r.staff}</div>
            <div className="h" style={{ width: 123, textAlign: "center", fontSize: 16 }}>{r.cash}</div>
          </Row>
        </Row>
      ))}
    </div>
  </Frame>
);

/* ------------------------------------------------------------------ *
 * 10 — Who it's for
 * ------------------------------------------------------------------ */
const WEDGE = [
  { ic: Sparkles, l: "Beauty & personal care", on: true },
  { ic: Utensils, l: "Food & beverage" },
  { ic: SprayCan, l: "Household & cleaning" },
  { ic: Shirt, l: "Apparel & accessories" },
  { ic: PawPrint, l: "Pet supplies" },
  { ic: Pill, l: "Supplements & wellness" },
  { ic: Baby, l: "Toys & baby" },
  { ic: Headphones, l: "Electronics accessories" },
];

const S10 = () => (
  <Frame
    n={10}
    eyebrow="WHO IT’S FOR"
    title={<>Built for the<span className="lime"> $1M–$200M seller.</span></>}
  >
    <div style={{ position: "absolute", left: 43.2, top: 190, width: 873.6, height: 90 }}>
      <div className="an" style={{ position: "absolute", left: 108, top: 0, fontSize: 12.5, color: "#8ea096", "--d": "200ms" }}>Spreadsheets</div>
      <div className="h an lime" style={{ position: "absolute", left: 432, top: -2, fontSize: 15, "--d": "260ms" }}>Verqo</div>
      <div className="an" style={{ position: "absolute", left: 748, top: 0, fontSize: 12.5, color: "#8ea096", "--d": "200ms" }}>SAP</div>
      <div style={{ position: "absolute", left: 21.6, top: 50, width: 828, height: 1, background: "rgba(255,255,255,.14)" }} />
      <div
        className="gw"
        style={{
          position: "absolute", left: 194, top: 45, height: 11, borderRadius: 999, background: LIME,
          "--w": "454px", "--d": "340ms",
        }}
      />
      {[[21.6, "$0"], [194, "$1M"], [648, "$200M"], [849, "$1B+"]].map(([x, l], i) => (
        <React.Fragment key={l}>
          <div style={{ position: "absolute", left: x - 4.5, top: 46, width: 9, height: 9, borderRadius: 999, background: i === 1 || i === 2 ? OFFWHITE : "#5c6e65" }} />
          <Mono size={9} color="#8ea096" style={{ position: "absolute", left: x - 4, top: 66 }}>{l}</Mono>
        </React.Fragment>
      ))}
    </div>

    <div className="h an" style={{ position: "absolute", left: 0, right: 0, top: 290, textAlign: "center", fontSize: 15, "--d": "440ms" }}>
      Too big for spreadsheets.<span className="lime"> Too small for SAP.</span>
    </div>

    <Body top={334} style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
      {WEDGE.map((w, i) => {
        const Ic = w.ic;
        return (
          <div
            key={w.l}
            className={`${w.on ? "vpd-card-a" : "vpd-card"} an`}
            style={{
              width: 209.4, height: 60, display: "flex", alignItems: "center", gap: 11, padding: "0 14px",
              position: "relative", "--d": `${480 + i * 55}ms`,
            }}
          >
            {w.on && (
              <span
                className="vpd-pill"
                style={{ position: "absolute", top: -9, left: 62, background: LIME, color: INK, fontSize: 8, padding: "3px 9px", fontWeight: 500 }}
              >
                OUR WEDGE
              </span>
            )}
            <Ic size={19} color={w.on ? LIME : "#7d8f85"} strokeWidth={1.6} />
            <span style={{ fontSize: 12, color: w.on ? "#fff" : "#9fb0a6", fontWeight: w.on ? 700 : 400 }}>{w.l}</span>
          </div>
        );
      })}
    </Body>
  </Frame>
);

/* ------------------------------------------------------------------ *
 * 11 — Proof
 * ------------------------------------------------------------------ */
const S11 = () => (
  <Frame
    n={11}
    eyebrow="PROOF"
    title={<>Customer zero is our own.<span className="lime"> Now live on Verqo.</span></>}
    footnote={
      <>
        <span>BRND before Verqo:&nbsp;</span>
        <b style={{ color: "#fff" }}>60 days</b> to get paid · <b style={{ color: "#fff" }}>2%</b> bad debt ·{" "}
        <b style={{ color: "#fff" }}>Net 15–30</b> terms&nbsp;&nbsp;→&nbsp;&nbsp;on Verqo:{" "}
        <b className="lime">paid Day 1</b> · <b className="lime">0%</b> bad debt on the seller
      </>
    }
    footnoteTop={476}
  >
    <Body top={172} style={{ display: "flex", gap: 16 }}>
      <div className="vpd-card an" style={{ width: 280.5, height: 288, padding: "22px 23px", display: "flex", flexDirection: "column", "--d": "200ms" }}>
        <Mono size={9}>CUSTOMER ZERO · WHERE VERQO CAME FROM</Mono>
        <img src="/pitch-assets/brand_brnd.png" alt="BRND Direct" style={{ height: 30, width: "auto", alignSelf: "flex-start", marginTop: 22 }} />
        <div style={{ fontSize: 12, color: "#cfdcd4", marginTop: 20, lineHeight: "17px" }}>
          The founder’s wholesale distributor. Every pain Verqo fixes, we felt here first.
        </div>
        <span className="vpd-pill" style={{ marginTop: "auto", background: "#0a1a15", border: "1px solid rgba(255,255,255,.1)", color: "#8ea096", fontSize: 8.5, padding: "6px 11px", alignSelf: "flex-start" }}>
          NOT COUNTED AS VERQO REVENUE
        </span>
      </div>

      <div className="vpd-card-a an" style={{ width: 280.5, height: 288, padding: "22px 23px", display: "flex", flexDirection: "column", "--d": "300ms" }}>
        <Mono size={9} color={LIME}>LIVE ON VERQO TODAY</Mono>
        <div className="h lime" style={{ fontSize: 38, marginTop: 20 }}>$40K / mo</div>
        <Row style={{ marginTop: 22, gap: 42 }}>
          {[["2", "orders / month"], ["3", "customers"]].map(([v, l]) => (
            <div key={l}>
              <div className="h" style={{ fontSize: 20 }}>{v}</div>
              <div style={{ fontSize: 10.5, color: "#9fb0a6", marginTop: 6 }}>{l}</div>
            </div>
          ))}
        </Row>
        <div style={{ fontSize: 10.5, color: "#8ea096", marginTop: "auto", lineHeight: "14px" }}>
          Amazon vendor · TikTok Shop seller · Whatnot seller
        </div>
      </div>

      <div className="vpd-card an" style={{ width: 280.5, height: 288, padding: "22px 23px", display: "flex", flexDirection: "column", "--d": "400ms" }}>
        <Mono size={9}>CATEGORY PROOF · HOKODO</Mono>
        <Row style={{ marginTop: 20, gap: 28 }}>
          {[["+40%", "conversion"], ["+30%", "order size"]].map(([v, l]) => (
            <div key={l}>
              <div className="h" style={{ fontSize: 28 }}>{v}</div>
              <div style={{ fontSize: 10.5, color: "#9fb0a6", marginTop: 7 }}>{l}</div>
            </div>
          ))}
        </Row>
        <div style={{ fontSize: 12, color: "#cfdcd4", marginTop: 26 }}>when B2B buyers get terms</div>
        <Mono size={8.5} color="#5c6e65" style={{ marginTop: "auto" }}>COMPANY’S OWN CLAIMS</Mono>
      </div>
    </Body>
  </Frame>
);

/* ------------------------------------------------------------------ *
 * 12 — Traction
 * ------------------------------------------------------------------ */
const PARTNERS = [
  { f: "logo_magnify.png", n: "Magnify Global", v: "est. $5M / yr", c: "Beauty brands", pad: "#f5f3ee" },
  { f: "logo_ami.png", n: "AMI Distribution", v: "est. $5M / yr", c: "HBA distributor", pad: "#14302a" },
  { f: "logo_ghost.png", n: "ghost", v: "est. $40M / yr", c: "B2B marketplace", pad: "#14302a" },
  { f: "logo_ashley.png", n: "Ashley Stewart", v: "est. $100M / yr", c: "Plus-size retail", pad: "#f5f3ee" },
  { f: "logo_online.png", n: "Online retail", v: "est. $5M / yr", c: "Online retail", pad: "#f5f3ee" },
  { f: "logo_svelte.png", n: "Svelte Brands", v: "est. $8M / yr", c: "Brand portfolio", pad: "#f5f3ee" },
  { f: "logo_allied.png", n: "Allied Luxury", v: "est. $5M / yr", c: "Luxury wholesale", pad: "#14302a" },
  { f: "logo_aec.png", n: "AEC Brands Group", v: "est. $5M / yr", c: "HBA wholesale", pad: "#14302a" },
  { f: "logo_ecscott.png", n: "EC Scott Beauty", v: "est. $22M / yr", c: "Prestige beauty", pad: "#f5f3ee" },
];

const S12 = () => (
  <Frame
    n={12}
    eyebrow="TRACTION FROM DAY 1"
    title={<>9 wholesale companies<span className="lime"> ready to sign up.</span></>}
  >
    <div style={{ position: "absolute", left: 43.2, top: 172, width: 577, display: "flex", flexWrap: "wrap", gap: 12 }}>
      {PARTNERS.map((p, i) => (
        <div key={p.n} className="vpd-card an" style={{ width: 184.3, height: 96, padding: 11, "--d": `${190 + i * 55}ms` }}>
          <div style={{ height: 40, borderRadius: 7, background: p.pad, overflow: "hidden", display: "grid", placeItems: "center" }}>
            <img src={`/pitch-assets/logos/${p.f}`} alt={p.n} style={{ width: "100%", height: "100%", objectFit: "contain" }} />
          </div>
          <Row style={{ marginTop: 11, justifyContent: "space-between", alignItems: "baseline" }}>
            <span style={{ fontSize: 10.5, fontWeight: 700 }}>{p.v}</span>
            <span style={{ fontSize: 9.5, color: "#7d8f85" }}>{p.c}</span>
          </Row>
        </div>
      ))}
    </div>

    <div className="vpd-card-a an" style={{ position: "absolute", left: 648, top: 172, width: 268.8, height: 308, padding: "22px 24px", display: "flex", flexDirection: "column", "--d": "300ms" }}>
      <Mono size={9} color="#8ea096">EST. COMBINED SALES</Mono>
      <div className="h" style={{ fontSize: 30, marginTop: 14 }}>
        $<Count to={195} delay={420} fmt={(v) => Math.round(v)} />M / yr
      </div>
      <div className="vpd-rule" style={{ margin: "22px 0 18px" }} />
      <Mono size={9} color="#8ea096">IF 10% RUNS THROUGH VERQO</Mono>
      <div className="h lime" style={{ fontSize: 30, marginTop: 14 }}>
        $<Count to={20} delay={620} fmt={(v) => Math.round(v)} />M GMV / yr
      </div>
      <span className="vpd-pill" style={{ marginTop: "auto", background: "#0a1a15", border: "1px solid rgba(255,255,255,.1)", color: "#9fb0a6", fontSize: 8.5, padding: "6px 11px", alignSelf: "flex-start" }}>
        COMMITTED PER FOUNDER
      </span>
      <div style={{ fontSize: 11, color: "#8ea096", marginTop: 12 }}>No signed LOIs yet</div>
    </div>
  </Frame>
);

/* ------------------------------------------------------------------ *
 * 13 — The plan
 * ------------------------------------------------------------------ */
const PLAN_PTS = [
  { x: 118, v: 0.04 },
  { x: 200, v: 15.7 },
  { x: 296, v: 40 },
  { x: 490, v: 159 },
  { x: 686, v: 310 },
  { x: 881, v: 524 },
];
const yOf = (v) => 436 - (v / 600) * 256;

const S13 = () => {
  const d = PLAN_PTS.map((p, i) => `${i ? "L" : "M"}${p.x} ${yOf(p.v)}`).join(" ");
  return (
    <Frame
      n={13}
      eyebrow="THE PLAN · 24 MONTHS"
      title={<>From $40K a month today<span className="lime"> to $524M a month in 24.</span></>}
      footnote={
        <>
          Revenue run-rate&nbsp; <b style={{ color: "#fff" }}>$8.0M M12 → $25.9M M24</b>
          &nbsp;&nbsp;&nbsp;of which SaaS ARR&nbsp; <b style={{ color: "#fff" }}>$1.7M → $5.4M</b>
          &nbsp;&nbsp;&nbsp;EBITDA+&nbsp; <b className="lime">Jun ’27</b>
        </>
      }
      footnoteTop={464}
    >
      <svg width="960" height="470" style={{ position: "absolute", left: 0, top: 0 }}>
        <defs>
          <linearGradient id="vpArea" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={LIME} stopOpacity="0.26" />
            <stop offset="100%" stopColor={LIME} stopOpacity="0" />
          </linearGradient>
        </defs>
        {[600, 400, 200, 0].map((g) => (
          <line key={g} x1={110} y1={yOf(g)} x2={895} y2={yOf(g)} stroke="rgba(255,255,255,.08)" strokeWidth="1" />
        ))}
        <line x1={118} y1={174} x2={118} y2={436} stroke={LIME} strokeWidth="1.4" strokeDasharray="5 5" opacity="0.75" />
        <path d={`${d} L881 ${yOf(0)} L118 ${yOf(0)} Z`} fill="url(#vpArea)" className="fade" style={{ "--d": "620ms" }} />
        <path
          d={d}
          fill="none" stroke={LIME} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"
          className="dash" style={{ "--len": 1100, "--d": "260ms" }}
        />
        {[0, 1, 3, 5].map((pi, k) => {
          const p = PLAN_PTS[pi];
          return (
            <circle
              key={p.x}
              cx={p.x} cy={yOf(p.v)} r={7}
              fill={pi === 0 ? OFFWHITE : LIME}
              className="an-s"
              style={{ "--d": `${700 + k * 130}ms`, transformOrigin: `${p.x}px ${yOf(p.v)}px` }}
            />
          );
        })}
      </svg>

      {[["$600M", 176], ["$400M", 261.2], ["$200M", 346.4]].map(([l, t]) => (
        <Mono key={l} size={8.5} color="#5c6e65" style={{ position: "absolute", left: 64.2, top: t }}>{l}</Mono>
      ))}
      <Mono size={8.5} color="#5c6e65" style={{ position: "absolute", left: 79.7, top: 431.6 }}>$0</Mono>

      <div className="m an" style={{ position: "absolute", left: 126.7, top: 174, fontSize: 8, letterSpacing: "0.14em", color: "#9fb0a6", background: "#13231d", padding: "4px 8px", borderRadius: 4, "--d": "300ms" }}>
        SEED CLOSES
      </div>
      <div className="h an" style={{ position: "absolute", left: 120.7, top: 393, fontSize: 10, "--d": "760ms" }}>$40K today</div>
      <div className="h an lime" style={{ position: "absolute", left: 203.9, top: 400, fontSize: 13, "--d": "840ms" }}>M3 · $15.7M</div>
      <div className="h an" style={{ position: "absolute", left: 454.6, top: 337, fontSize: 13, "--d": "920ms" }}>$159M</div>
      <div className="h an lime" style={{ position: "absolute", left: 827.5, top: 182, fontSize: 13, "--d": "1000ms" }}>$524M</div>

      {[["Today", 84.3], ["M3", 190.1], ["M6", 288.2], ["M12", 481.8], ["M18", 678], ["M24", 874.2]].map(([l, x]) => (
        <Mono key={l} size={8.5} color="#5c6e65" style={{ position: "absolute", left: x, top: 447.4 }}>{l}</Mono>
      ))}
    </Frame>
  );
};

/* ------------------------------------------------------------------ *
 * 14 — Market · why now
 * ------------------------------------------------------------------ */
const MARKET = [
  { v: "$8.4T", d: "US wholesale trade", y: 190, r: 130, c: "#16302a", cx: 237 },
  { v: "$2.3T", d: "US B2B sold online", y: 258, r: 98, c: "#1d4238", cx: 265 },
  { v: "$110B", d: "Beauty & personal care · our wedge", y: 328, r: 62, c: GREEN, cx: 283 },
  { v: "$4.2B", d: "Year-2 GMV in plan", y: 398, r: 29, c: LIME, cx: 293, lime: true },
];
const WHYNOW = [
  { ic: Zap, t: "Instant rails", s: "RTP 2017 · FedNow 2023" },
  { ic: Handshake, t: "Buyers expect terms", s: "Faire taught Net 60" },
  { ic: BrainCircuit, t: "AI underwriting", s: "Small invoices now worth scoring" },
];

const S14 = () => (
  <Frame
    n={14}
    eyebrow="MARKET · WHY NOW"
    size={30}
    title={<>An $8.4T market.<span className="lime"> 73% still offline: email, PDFs, phone.</span></>}
  >
    <div style={{ position: "absolute", left: 0, top: 165, width: 700, height: 315 }}>
      {MARKET.map((m, i) => (
        <div
          key={m.v}
          className="an-s"
          style={{
            position: "absolute", left: m.cx - m.r, top: 313 - m.r * 2, width: m.r * 2, height: m.r * 2,
            borderRadius: 999, background: m.c, "--d": `${200 + i * 110}ms`,
          }}
        />
      ))}
      <svg width="700" height="315" style={{ position: "absolute", inset: 0 }}>
        {MARKET.map((m, i) => (
          <line
            key={m.v}
            x1={m.cx + (i < 2 ? m.r * 0.62 : m.r * 0.9)} y1={313 - m.r * 2 + (i < 2 ? m.r * 0.22 : m.r * 0.72)}
            x2={408} y2={m.y - 165 + 10}
            stroke="rgba(255,255,255,.2)" strokeWidth="1"
            className="dash" style={{ "--len": 300, "--d": `${480 + i * 90}ms` }}
          />
        ))}
      </svg>
      {MARKET.map((m, i) => (
        <React.Fragment key={m.v}>
          <div className={`h an ${m.lime ? "lime" : ""}`} style={{ position: "absolute", left: 420, top: m.y - 177, fontSize: 22, "--d": `${520 + i * 90}ms` }}>{m.v}</div>
          <div className="an" style={{ position: "absolute", left: 512, top: m.y - 172, width: 160, fontSize: 11.5, color: "#9fb0a6", lineHeight: "15px", "--d": `${560 + i * 90}ms` }}>{m.d}</div>
        </React.Fragment>
      ))}
    </div>

    <Mono size={9} color={LIME} style={{ position: "absolute", left: 700, top: 174 }}>WHY NOW</Mono>
    <div style={{ position: "absolute", left: 700, top: 200, width: 216.8 }}>
      {WHYNOW.map((w, i) => {
        const Ic = w.ic;
        return (
          <div key={w.t} className="vpd-card an" style={{ height: 76, marginBottom: 12, padding: "14px 16px", "--d": `${340 + i * 110}ms` }}>
            <Ic size={19} color={LIME} strokeWidth={1.6} />
            <div className="h" style={{ fontSize: 13, marginTop: 9 }}>{w.t}</div>
            <div style={{ fontSize: 10.5, color: "#8ea096", marginTop: 6 }}>{w.s}</div>
          </div>
        );
      })}
    </div>
  </Frame>
);

/* ------------------------------------------------------------------ *
 * 15 — Business model
 * ------------------------------------------------------------------ */
const REV = [
  { n: "Financing", r: "0.25% / 30 days", a: "$6.61M", v: 6.61, c: LIME },
  { n: "Shipping", r: "5% markup", a: "$4.61M", v: 4.61, c: GREEN },
  { n: "Payments & payouts", r: "0.3–0.5% payout", a: "$4.32M", v: 4.32, c: GREEN_2 },
  { n: "Subscriptions", r: "$99 → $3,999 / mo", a: "$4.30M", v: 4.3, c: OFFWHITE },
  { n: "Insurance + apps", r: "7% of premium", a: "$218K", v: 0.218, c: "#8c9d94" },
  { n: "Dropship", r: "per order", a: "$186K", v: 0.186, c: "#5f7068" },
];

const S15 = () => (
  <Frame
    n={15}
    eyebrow="BUSINESS MODEL"
    size={30}
    title={<>Buyers free. Sellers pay.<span className="lime"> We earn on the money that moves.</span></>}
  >
    <svg width="360" height="300" style={{ position: "absolute", left: 20, top: 175 }}>
      <Donut cx={176} cy={148} r={110} w={52} data={REV.map((r) => ({ v: r.v, c: r.c }))} />
      <text x={176} y={146} textAnchor="middle" className="h" style={{ fontSize: 26, fill: "#fff" }}>$20.2M</text>
      <text x={176} y={165} textAnchor="middle" style={{ fontSize: 10, fill: "#8ea096", fontFamily: "DM Sans" }}>24-month revenue</text>
    </svg>

    <div style={{ position: "absolute", left: 397, top: 182, width: 519.8 }}>
      {REV.map((r, i) => (
        <Row key={r.n} className="an" style={{ height: 45, "--d": `${280 + i * 85}ms` }}>
          <Dot c={r.c} s={11} />
          <span className="h" style={{ fontSize: 13, marginLeft: 16, width: 196 }}>{r.n}</span>
          <Mono size={10} color="#8ea096" style={{ width: 178 }}>{r.r}</Mono>
          <span className="h" style={{ fontSize: 15, flex: 1, textAlign: "right" }}>{r.a}</span>
        </Row>
      ))}
      <Mono size={8.5} color="#5c6e65" style={{ marginTop: 18 }}>6 REVENUE LAYERS · MODEL V6, NOV ’26 – OCT ’28</Mono>
    </div>
  </Frame>
);

/* ------------------------------------------------------------------ *
 * 16 — Unit economics
 * ------------------------------------------------------------------ */
const TIERS = [
  { k: "STARTER · $99", v: "$1K", gp: "$880", ch: "4.0%", ltv: "$22K", b: "2 buyers incl.", pb: "+$30 / buyer" },
  { k: "GROWTH · $599", v: "$6K", gp: "$4K", ch: "2.5%", ltv: "$176K", b: "10 buyers incl.", pb: "+$25 / buyer" },
  { k: "PRO · $1,499", v: "$14K", gp: "$9K", ch: "2.0%", ltv: "$466K", b: "50 buyers incl.", pb: "+$20 / buyer" },
  { k: "ENTERPRISE · $3,999", v: "$28K", gp: "$19K", ch: "1.5%", ltv: "$1.3M", b: "100 buyers incl.", pb: "+$15 / buyer", on: true },
];
const UE = [["$4.0K", "CAC per seller, fully loaded"], ["0.9 mo", "CAC payback"], ["28×", "LTV : CAC"], ["69%", "gross margin"], ["85%", "net revenue retention"]];

const S16 = () => (
  <Frame
    n={16}
    eyebrow="UNIT ECONOMICS"
    title={<>Priced per buyer.<span className="lime"> CAC back in under a month.</span></>}
  >
    <Row style={{ position: "absolute", left: 43.2, top: 172, gap: 12.5 }}>
      {TIERS.map((t, i) => (
        <div
          key={t.k}
          className={`${t.on ? "vpd-card-a" : "vpd-card"} an`}
          style={{ width: 155, height: 300, padding: "18px 16px", display: "flex", flexDirection: "column", "--d": `${190 + i * 85}ms` }}
        >
          <Mono size={8.5} color={t.on ? LIME : "#7d8f85"} ls="0.1em">{t.k}</Mono>
          <div className="h" style={{ fontSize: 30, marginTop: 14 }}>{t.v}</div>
          <div style={{ fontSize: 10, color: "#8ea096", marginTop: 7 }}>revenue / seller / mo</div>
          <div className="vpd-rule" style={{ margin: "16px 0 13px" }} />
          <div style={{ fontSize: 11, color: "#8ea096" }}>Gross profit <b style={{ color: "#fff" }}>{t.gp}</b></div>
          <div style={{ fontSize: 11, color: "#8ea096", marginTop: 9 }}>Churn / mo <b style={{ color: "#fff" }}>{t.ch}</b></div>
          <Row style={{ marginTop: 14, gap: 7, alignItems: "baseline" }}>
            <Mono size={9} color="#8ea096">LTV</Mono>
            <span className="h lime" style={{ fontSize: 20 }}>{t.ltv}</span>
          </Row>
          <div style={{ marginTop: "auto", background: "#0a1a15", borderRadius: 9, padding: "10px 11px" }}>
            <Row style={{ gap: 7 }}>
              <Users size={14} color={LIME} strokeWidth={1.6} />
              <span style={{ fontSize: 10, color: "#9fb0a6" }}>{t.b}</span>
            </Row>
            <div style={{ fontSize: 11, fontWeight: 700, marginTop: 8 }}>{t.pb}</div>
          </div>
        </div>
      ))}
    </Row>

    <div style={{ position: "absolute", left: 705, top: 180, width: 1, height: 292, background: "rgba(255,255,255,.1)" }} />
    <div style={{ position: "absolute", left: 723, top: 184 }}>
      {UE.map(([v, l], i) => (
        <Row key={l} className="an" style={{ height: 60, gap: 16, "--d": `${340 + i * 90}ms` }}>
          <span className="h" style={{ fontSize: 22, width: 82 }}>{v}</span>
          <span style={{ fontSize: 11, color: "#9fb0a6", width: 105, lineHeight: "14px" }}>{l}</span>
        </Row>
      ))}
    </div>
  </Frame>
);

/* ------------------------------------------------------------------ *
 * 17 — Go-to-market
 * ------------------------------------------------------------------ */
const GTM = [
  { v: 5, m: "M1", c: "BRND + 4 beta" },
  { v: 16, m: "M3", c: "Founder’s 30+ network" },
  { v: 34, m: "M6", c: "A community we belong to" },
  { v: 116, m: "M12", c: "Partners scale it" },
  { v: 346, m: "M24", c: "Partner engine" },
];
const CHANNELS = [
  { ic: Calculator, t: "CFOs & accountants", s: "25% of SaaS for 2 yrs" },
  { ic: Umbrella, t: "Insurance partners", s: "intros to their clients" },
  { ic: Truck, t: "Shipping + 3PL", s: "their brands onboard" },
  { ic: Building2, t: "Lending brokers", s: "buyers needing terms" },
];

const S17 = () => (
  <Frame
    n={17}
    eyebrow="GO-TO-MARKET"
    title={<>Our network first.<span className="lime"> Then partners bring the next seller.</span></>}
  >
    <Mono size={9} color="#7d8f85" style={{ position: "absolute", left: 43.2, top: 176 }}>ACTIVE SELLERS</Mono>

    <div style={{ position: "absolute", left: 43.2, top: 205, width: 577, height: 275 }}>
      {GTM.map((g, i) => {
        const h = Math.max(9, 222 * Math.pow(g.v / 346, 0.65));
        const last = i === 4;
        return (
          <div key={g.m} style={{ position: "absolute", left: i * 120, top: 0, width: 95 }}>
            <div
              className="h an"
              style={{ position: "absolute", top: 237 - h - 30, width: 95, textAlign: "center", fontSize: 20, color: last ? LIME : "#fff", "--d": `${520 + i * 100}ms` }}
            >
              <Count to={g.v} delay={560 + i * 100} fmt={(v) => Math.round(v)} />
            </div>
            <div
              className="gh"
              style={{
                position: "absolute", top: 237 - h, width: 95, height: h, borderRadius: "4px 4px 0 0",
                background: last ? LIME : i === 3 ? GREEN : "#16302a",
                "--h": `${h}px`, "--d": `${300 + i * 100}ms`,
              }}
            />
            <Mono size={9} color="#5c6e65" style={{ position: "absolute", top: 246, width: 95, textAlign: "center" }}>{g.m}</Mono>
            <div className="an" style={{ position: "absolute", top: 264, width: 95, textAlign: "center", fontSize: 10.5, color: "#9fb0a6", lineHeight: "14px", "--d": `${640 + i * 100}ms` }}>{g.c}</div>
          </div>
        );
      })}
    </div>

    <div className="vpd-card an" style={{ position: "absolute", left: 648, top: 172, width: 268.8, height: 298, padding: "20px 22px", "--d": "280ms" }}>
      <Mono size={9} color={LIME}>PARTNER CHANNELS</Mono>
      <div style={{ marginTop: 16 }}>
        {CHANNELS.map((c, i) => {
          const Ic = c.ic;
          return (
            <Row key={c.t} className="an" style={{ gap: 13, marginBottom: 22, alignItems: "flex-start", "--d": `${400 + i * 95}ms` }}>
              <Ic size={19} color={LIME} strokeWidth={1.6} style={{ marginTop: 2 }} />
              <div>
                <div className="h" style={{ fontSize: 12.5 }}>{c.t}</div>
                <div style={{ fontSize: 10, color: "#8ea096", marginTop: 6 }}>{c.s}</div>
              </div>
            </Row>
          );
        })}
      </div>
    </div>
  </Frame>
);

/* ------------------------------------------------------------------ *
 * 18 — Competition
 * ------------------------------------------------------------------ */
const COMPS = ["Verqo", "Wholesail", "TreviPay", "Resolve", "Faire", "Slope", "Balance"];
const MATRIX = [
  ["Net terms for buyers", ["y", "p", "w", "w", "w", "w", "w"]],
  ["Seller paid fast", ["y", "p", "w", "w", "w", "p", "w"]],
  ["Wholesale ordering", ["y", "n", "n", "n", "w", "n", "p"]],
  ["Shipping + insurance built in", ["y", "n", "n", "n", "p", "n", "n"]],
  ["Bank account + instant payout", ["y", "n", "n", "n", "n", "n", "n"]],
];

const Cell = ({ k }) => {
  if (k === "n") return <span style={{ width: 13, height: 1.6, background: "#45574e", display: "block" }} />;
  if (k === "p") return <span style={{ width: 13, height: 13, borderRadius: 999, border: "1.6px solid #c6d4cb", display: "block" }} />;
  return <Dot c={k === "y" ? LIME : OFFWHITE} s={14} />;
};

const S18 = () => (
  <Frame
    n={18}
    eyebrow="COMPETITION"
    title={<>Others own a slice.<span className="lime"> Verqo owns the whole order.</span></>}
    footnote={
      <Row style={{ gap: 20 }}>
        <Row style={{ gap: 7 }}><Dot c="#8ea096" s={7} /><Mono size={9}>yes</Mono></Row>
        <Row style={{ gap: 7 }}><span style={{ width: 7, height: 7, borderRadius: 999, border: "1px solid #8ea096" }} /><Mono size={9}>partial</Mono></Row>
        <Row style={{ gap: 7 }}><span style={{ width: 9, height: 1, background: "#8ea096" }} /><Mono size={9}>no</Mono></Row>
      </Row>
    }
    footnoteTop={470}
  >
    <div className="an-s" style={{ position: "absolute", left: 311, top: 166, width: 87, height: 292, borderRadius: 12, background: "rgba(220,255,58,.05)", border: `1.4px solid ${LIME}`, "--d": "220ms" }} />

    <div style={{ position: "absolute", left: 43.2, top: 176, width: 873.6 }}>
      <Row className="an" style={{ height: 44, "--d": "260ms" }}>
        <span style={{ width: 267 }} />
        {COMPS.map((c, i) => (
          <span key={c} className="h" style={{ width: 87, textAlign: "center", fontSize: 12, color: i === 0 ? LIME : "#dfe8e2" }}>{c}</span>
        ))}
      </Row>
      {MATRIX.map(([label, cells], r) => (
        <div key={label} className="an" style={{ "--d": `${320 + r * 85}ms` }}>
          <div className="vpd-rule" />
          <Row style={{ height: 46.4 }}>
            <span style={{ width: 267, fontSize: 12, color: "#dfe8e2" }}>{label}</span>
            {cells.map((k, i) => (
              <span key={i} style={{ width: 87, display: "grid", placeItems: "center" }}><Cell k={k} /></span>
            ))}
          </Row>
        </div>
      ))}
    </div>
  </Frame>
);

/* ------------------------------------------------------------------ *
 * 19 — Who carries the risk
 * ------------------------------------------------------------------ */
const ORDER_STEPS = [
  { ic: Store, t: "Buyer orders on Net 60" },
  { ic: Zap, t: "Verqo pays seller Day 1" },
  { ic: Wallet, t: "Buyer pays Verqo Day 60" },
];
const WATERFALL = [
  { n: "1", t: "Verqo reserve", s: "0.30% first loss", bg: LIME, fg: INK, w: 282, x: 0 },
  { n: "2", t: "Credit insurance", s: "Allianz Trade", bg: GREEN_2, fg: "#fff", w: 258, x: 16 },
  { n: "3", t: "Credit partner", s: "TBD", bg: GREEN, fg: "#fff", w: 234, x: 32 },
];

const S19 = () => (
  <Frame
    n={19}
    eyebrow="WHO CARRIES THE RISK"
    size={30}
    title={<>Seller paid Day 1.<span className="lime"> Buyer default never lands on them.</span></>}
    footnote={
      <>
        Funds held at <b style={{ color: "#fff" }}>Cross River Bank</b>&nbsp; ·&nbsp;{" "}
        <b style={{ color: "#fff" }}>$2.5M → $7.5M reserve facility</b> covers the bank’s ACH hold
      </>
    }
    footnoteTop={468}
  >
    <div className="vpd-card an" style={{ position: "absolute", left: 43.2, top: 172, width: 260, height: 274, padding: "20px 22px", "--d": "190ms" }}>
      <Mono size={9}>AN ORDER ON TERMS</Mono>
      <div style={{ marginTop: 20 }}>
        {ORDER_STEPS.map((s, i) => {
          const Ic = s.ic;
          return (
            <div key={s.t} className="an" style={{ "--d": `${300 + i * 110}ms` }}>
              <Row style={{ gap: 13 }}>
                <div style={{ width: 36, height: 36, borderRadius: 999, background: "#0a1a15", display: "grid", placeItems: "center", flex: "0 0 auto" }}>
                  <Ic size={17} color={LIME} strokeWidth={1.6} />
                </div>
                <span style={{ fontSize: 12, color: "#dfe8e2" }}>{s.t}</span>
              </Row>
              {i < 2 && (
                <svg width="10" height="34" style={{ marginLeft: 13 }}>
                  <line x1="5" y1="2" x2="5" y2="26" stroke={LIME} strokeWidth="1.2" opacity="0.7" />
                  <path d="M1 24 L5 30 L9 24" fill="none" stroke={LIME} strokeWidth="1.2" opacity="0.7" />
                </svg>
              )}
            </div>
          );
        })}
      </div>
    </div>

    <div className="vpd-card an" style={{ position: "absolute", left: 320, top: 172, width: 324, height: 274, padding: "20px 21px", "--d": "280ms" }}>
      <Mono size={9} color={LIME}>IF A BUYER DEFAULTS, LOSS GOES TO</Mono>
      <div style={{ marginTop: 18 }}>
        {WATERFALL.map((w, i) => (
          <div
            key={w.n}
            className="an"
            style={{
              marginLeft: w.x, width: w.w, height: 58, borderRadius: 10, background: w.bg, color: w.fg,
              display: "flex", alignItems: "center", gap: 14, padding: "0 16px", marginBottom: 10,
              "--d": `${380 + i * 120}ms`,
            }}
          >
            <span className="h" style={{ fontSize: 20 }}>{w.n}</span>
            <div>
              <div className="h" style={{ fontSize: 13 }}>{w.t}</div>
              <div style={{ fontSize: 10.5, opacity: 0.82, marginTop: 4 }}>{w.s}</div>
            </div>
          </div>
        ))}
      </div>
    </div>

    <div className="an" style={{ position: "absolute", left: 648, top: 298, "--d": "700ms" }}>
      <ArrowRight size={18} color={LIME} strokeWidth={2} />
    </div>

    <div className="vpd-card-a an" style={{ position: "absolute", left: 674, top: 172, width: 242.8, height: 274, padding: "22px 24px", display: "flex", flexDirection: "column", "--d": "360ms" }}>
      <ShieldCheck size={38} color={LIME} strokeWidth={1.5} />
      <div className="h lime" style={{ fontSize: 19, marginTop: 20 }}>Seller protected</div>
      <div style={{ fontSize: 12, color: "#cfdcd4", marginTop: 13, lineHeight: "17px" }}>
        Non-recourse: the Day-1 cash is theirs to keep
      </div>
      <span className="vpd-pill" style={{ marginTop: "auto", background: "#0a1a15", color: LIME, fontSize: 8.5, padding: "7px 12px", alignSelf: "flex-start" }}>
        0% BAD DEBT ON THE SELLER
      </span>
    </div>
  </Frame>
);

/* ------------------------------------------------------------------ *
 * 20 — Team
 * ------------------------------------------------------------------ */
const TEAM = [
  { i: "Z", n: "Zev Ostreicher", r: "FOUNDER & CEO", a: "14+ yrs wholesale · 5 companies", b: "BRND Direct · Zoganic", on: true },
  { i: "D", n: "David Levy", r: "HEAD OF OPERATIONS", a: "Onboarding · fulfilment", b: "Prior: Fabiwood" },
  { i: "B", n: "Basi Hershkowitz", r: "SALES DIRECTOR", a: "Founder-led sales, beauty network", b: "E-commerce experience" },
  { i: "D", n: "Dom Byno", r: "DATA & AUTOMATION", a: "Catalog AI · underwriting data", b: "E-commerce data expert" },
];

const S20 = () => (
  <Frame
    n={20}
    eyebrow="TEAM"
    title={<>We run wholesale.<span className="lime"> Now we’re building its rails.</span></>}
  >
    <Body top={172} style={{ display: "flex", gap: 12.8 }}>
      {TEAM.map((t, i) => (
        <div key={t.n} className="vpd-card an" style={{ width: 209.4, height: 208, padding: "22px 21px", "--d": `${190 + i * 85}ms` }}>
          <div
            style={{
              width: 54, height: 54, borderRadius: 999, display: "grid", placeItems: "center",
              background: t.on ? LIME : "#16302a",
            }}
          >
            <span className="h" style={{ fontSize: 20, color: t.on ? INK : LIME }}>{t.i}</span>
          </div>
          <div className="h" style={{ fontSize: 14, marginTop: 20 }}>{t.n}</div>
          <Mono size={9} color={LIME} style={{ marginTop: 9 }}>{t.r}</Mono>
          <div style={{ fontSize: 11, color: "#cfdcd4", marginTop: 14, lineHeight: "15px" }}>{t.a}</div>
          <div style={{ fontSize: 10.5, color: "#7d8f85", marginTop: 11 }}>{t.b}</div>
        </div>
      ))}
    </Body>

    <div className="vpd-card an" style={{ position: "absolute", left: 43.2, top: 395, width: 873.6, height: 79, padding: "0 24px", display: "flex", alignItems: "center", "--d": "560ms" }}>
      <PiggyBank size={26} color={LIME} strokeWidth={1.6} />
      <div style={{ marginLeft: 16, flex: 1 }}>
        <div className="h" style={{ fontSize: 14 }}>Bootstrapped to a live product</div>
        <div style={{ fontSize: 11, color: "#9fb0a6", marginTop: 6 }}>
          <b className="lime">$800K</b> invested · founder, friends &amp; family
        </div>
      </div>
      {[["$690K", "peak burn", false], ["5 → 19", "team, M1 → M24", false], ["Jun ’27", "EBITDA-positive", true]].map(([v, l, lime]) => (
        <div key={l} style={{ width: 168 }}>
          <div className={`h ${lime ? "lime" : ""}`} style={{ fontSize: 22 }}>{v}</div>
          <div style={{ fontSize: 10.5, color: "#8ea096", marginTop: 6 }}>{l}</div>
        </div>
      ))}
    </div>
  </Frame>
);

/* ------------------------------------------------------------------ *
 * 21 — The vision · roadmap
 * ------------------------------------------------------------------ */
const OWNERS = [
  { n: "Ramp", s: "Corporate cards", lg: "ramp.png" },
  { n: "Stripe", s: "Online checkout", lg: "stripe.png" },
  { n: "Shopify", s: "Ecommerce", lg: "shopify.png" },
];
const ROADMAP = [
  { y: "NOW · NOV ’26", t: "Live with BRND + beta", on: true },
  { y: "2027", t: "Terms + Day-1 pay · Series A" },
  { y: "2028", t: "Banking + cards · Series B" },
  { y: "2029+", t: "Marketplace · global rails" },
];

const S21 = () => (
  <Frame
    n={21}
    eyebrow="THE VISION · ROADMAP"
    title={<>Every category has an owner.<span className="lime"> Wholesale has none.</span></>}
    footnote={<span style={{ color: "#cfdcd4" }}>The #1 wholesale operating system: one network for every buyer and seller.</span>}
    footnoteTop={464}
  >
    <Body top={172} style={{ display: "flex", gap: 12.8 }}>
      {OWNERS.map((o, i) => (
        <div key={o.n} className="vpd-card an" style={{ width: 209.4, height: 108, padding: "20px 21px", "--d": `${190 + i * 85}ms` }}>
          <Row style={{ gap: 11 }}>
            <img src={`/pitch-assets/tools/${o.lg}`} alt={o.n} style={{ width: 28, height: 28, borderRadius: 7, flex: "0 0 auto" }} />
            <span className="h" style={{ fontSize: 22 }}>{o.n}</span>
          </Row>
          <div style={{ fontSize: 12, color: "#9fb0a6", marginTop: 14 }}>{o.s}</div>
        </div>
      ))}
      <div className="vpd-card-a an" style={{ width: 209.4, height: 108, padding: "20px 21px", "--d": "445ms" }}>
        <Row style={{ gap: 11 }}>
          <Lockup scale={1.26} />
        </Row>
        <div className="h lime" style={{ fontSize: 12, marginTop: 14 }}>Wholesale</div>
        <Mono size={8} color="#8ea096" style={{ marginTop: 7 }}>$8.4T · NO OWNER YET</Mono>
      </div>
    </Body>

    <Mono size={9} color="#7d8f85" style={{ position: "absolute", left: 43.2, top: 298 }}>ROADMAP</Mono>

    <div style={{ position: "absolute", left: 43.2, top: 326, width: 873.6 }}>
      <div
        className="gw"
        style={{ position: "absolute", left: 21.6, top: 46, height: 1, background: "rgba(255,255,255,.16)", "--w": "828px", "--d": "420ms" }}
      />
      <Row>
        {ROADMAP.map((r, i) => (
          <div key={r.y} className="an" style={{ width: 221.4, "--d": `${480 + i * 100}ms` }}>
            <Mono size={10} color={r.on ? LIME : "#6f8178"}>{r.y}</Mono>
            <div
              style={{
                width: 15, height: 15, borderRadius: 999, marginTop: 14,
                background: r.on ? LIME : i === 3 ? "transparent" : OFFWHITE,
                border: i === 3 ? `1.6px solid ${OFFWHITE}` : "none",
                boxShadow: r.on ? "0 0 0 4px rgba(220,255,58,.16)" : "none",
              }}
            />
            <div className="h" style={{ fontSize: 13, marginTop: 19 }}>{r.t}</div>
          </div>
        ))}
      </Row>
    </div>
  </Frame>
);

/* ------------------------------------------------------------------ *
 * 22 — Valuation path
 * ------------------------------------------------------------------ */
const VAL = [
  { v: "$12.5M", top: 405, x: 82, c: "transparent", border: true, l: "TODAY · SEED", n: 12.5 },
  { v: "~$80M", top: 320, x: 220, c: GREEN, err: true, l: "M12 · SERIES A", n: 80 },
  { v: "~$207M", top: 272, x: 357, c: GREEN, err: true, l: "M24 · SERIES B", n: 207 },
  { v: "$1B", top: 203, x: 494, c: LIME, l: "2029–30", lime: true, n: 1000 },
];
const MOVERS = ["Signed seller LOIs", "5+ live sellers beyond BRND", "Bank + credit partner signed", "NRR above 100%"];

const S22 = () => (
  <Frame
    n={22}
    eyebrow="VALUATION PATH"
    title={<>Priced on proof today.<span className="lime"> Built to compound to $1B.</span></>}
  >
    <div style={{ position: "absolute", left: 43.2, top: 180, width: 577, height: 290 }}>
      {[["$1B", 198], ["$100M", 304], ["$10M", 410]].map(([l, y]) => (
        <React.Fragment key={l}>
          <div style={{ position: "absolute", left: 32, top: y - 180, width: 545, height: 1, borderTop: "1px dotted rgba(255,255,255,.18)" }} />
          <Mono size={9} color="#5c6e65" style={{ position: "absolute", left: 0, top: y - 186 }}>{l}</Mono>
        </React.Fragment>
      ))}
      {VAL.map((b, i) => (
        <div key={b.v} style={{ position: "absolute", left: b.x, top: 0, width: 94 }}>
          <div
            className="h an"
            style={{ position: "absolute", top: b.top - 180 - (b.err ? 52 : 30), width: 94, textAlign: "center", fontSize: 17, color: b.lime ? LIME : "#fff", "--d": `${440 + i * 110}ms` }}
          >
            {b.v}
          </div>
          <div
            className="gh"
            style={{
              position: "absolute", top: b.top - 180, width: 94, height: 446 - b.top,
              background: b.c, border: b.border ? "1.4px solid rgba(220,255,58,.3)" : "none",
              borderRadius: "4px 4px 0 0", "--h": `${446 - b.top}px`, "--d": `${240 + i * 110}ms`,
            }}
          />
          {b.err && (
            <svg width="94" height="28" className="an" style={{ position: "absolute", top: b.top - 180 - 28, left: 0, "--d": `${560 + i * 110}ms` }}>
              <line x1="47" y1="2" x2="47" y2="26" stroke={OFFWHITE} strokeWidth="1.6" />
              <line x1="39" y1="2" x2="55" y2="2" stroke={OFFWHITE} strokeWidth="1.6" />
              <line x1="39" y1="26" x2="55" y2="26" stroke={OFFWHITE} strokeWidth="1.6" />
            </svg>
          )}
          <Mono size={9} color={b.lime ? "#dfe8e2" : "#5c6e65"} style={{ position: "absolute", top: 275, width: 94, textAlign: "center" }}>{b.l}</Mono>
        </div>
      ))}
    </div>

    <div className="vpd-card an" style={{ position: "absolute", left: 658, top: 172, width: 258.8, height: 298, padding: "20px 22px", display: "flex", flexDirection: "column", "--d": "300ms" }}>
      <Mono size={9} color={LIME}>WHAT MOVES US UP THE RANGE</Mono>
      <div style={{ marginTop: 18 }}>
        {MOVERS.map((m, i) => (
          <Row key={m} className="an" style={{ gap: 12, marginBottom: 22, alignItems: "flex-start", "--d": `${420 + i * 95}ms` }}>
            <Check size={16} color={LIME} strokeWidth={2.4} style={{ marginTop: 1, flex: "0 0 auto" }} />
            <span style={{ fontSize: 12.5, color: "#dfe8e2" }}>{m}</span>
          </Row>
        ))}
      </div>
      <div style={{ fontSize: 10.5, color: "#8ea096", marginTop: "auto" }}>$10M → ~$20M cap as these land</div>
    </div>
  </Frame>
);

/* ------------------------------------------------------------------ *
 * 23 — Use of funds
 * ------------------------------------------------------------------ */
const FUNDS = [
  { n: "Team & hiring", a: "$835K", v: 835, c: LIME },
  { n: "Runway & growth reserve", a: "$1.10M", v: 1100, c: OFFWHITE },
  { n: "Platform, banking & compliance", a: "$320K", v: 320, c: GREEN_2 },
  { n: "Fees & one-time", a: "$144K", v: 144, c: GREEN },
  { n: "ACH dispute insurance", a: "$62K", v: 62, c: "#8c9d94" },
  { n: "GTM & partners", a: "$38K", v: 38, c: "#5f7068" },
];

const S23 = () => (
  <Frame
    n={23}
    eyebrow="USE OF FUNDS"
    size={29}
    title={<>$2.5M equity runs the company.<span className="lime"> The facility is held, not spent.</span></>}
  >
    <svg width="340" height="300" style={{ position: "absolute", left: 12, top: 172 }}>
      <Donut cx={168} cy={146} r={108} w={52} data={FUNDS.map((f) => ({ v: f.v, c: f.c }))} />
      <text x={168} y={144} textAnchor="middle" className="h" style={{ fontSize: 24, fill: "#fff" }}>$2.5M</text>
      <text x={168} y={163} textAnchor="middle" style={{ fontSize: 10, fill: "#8ea096", fontFamily: "DM Sans" }}>equity</text>
    </svg>

    <div style={{ position: "absolute", left: 340, top: 180, width: 280 }}>
      {FUNDS.map((f, i) => (
        <Row key={f.n} className="an" style={{ height: 43, "--d": `${280 + i * 80}ms` }}>
          <Dot c={f.c} s={11} />
          <span style={{ fontSize: 12, marginLeft: 14, flex: 1, color: "#dfe8e2" }}>{f.n}</span>
          <span className="h" style={{ fontSize: 14 }}>{f.a}</span>
        </Row>
      ))}
      <div className="an" style={{ fontSize: 10.5, color: "#8ea096", marginTop: 18, lineHeight: "14px", "--d": "780ms" }}>
        <b style={{ color: "#fff" }}>Runway &amp; growth reserve:</b> ~5 months of opex if revenue lags, or faster hiring if it doesn’t
      </div>
    </div>

    <div className="vpd-card-a an" style={{ position: "absolute", left: 658, top: 172, width: 258.8, height: 288, padding: "20px 22px", "--d": "340ms" }}>
      <Mono size={9} color={LIME}>DEBT · $2.5M RESERVE FACILITY</Mono>
      <div className="h" style={{ fontSize: 36, marginTop: 18 }}>$2.5M</div>
      <div style={{ fontSize: 11.5, color: "#cfdcd4", marginTop: 13, lineHeight: "16px" }}>
        Funds the bank reserve behind Day-1 payouts
      </div>
      <div style={{ marginTop: 24, height: 14, borderRadius: 999, background: "#0a1a15", overflow: "hidden" }}>
        <div className="gw" style={{ height: 14, borderRadius: 999, background: LIME, "--w": "33%", "--d": "600ms" }} />
      </div>
      <Row style={{ marginTop: 12, gap: 8 }}>
        <Mono size={9} color={LIME}>$2.5M now</Mono>
        <ArrowRight size={11} color="#8ea096" />
        <Mono size={9} color="#8ea096">$7.5M accordion</Mono>
      </Row>
      <div style={{ fontSize: 11, color: "#cfdcd4", marginTop: 18 }}>Borrowed, repaid as each hold releases</div>
    </div>
  </Frame>
);

/* ------------------------------------------------------------------ *
 * 24 — Milestones + the ask
 * ------------------------------------------------------------------ */
const MILES = [
  { m: "M1", t: "Live with real orders", s: "5 sellers" },
  { m: "M3", t: "Terms + payouts on", s: "16 sellers" },
  { m: "M6", t: "A dense niche works", s: "$39.3M GMV / mo" },
  { m: "M12", t: "Raise the A", s: "116 sellers · $8.0M run-rate", x: "OF WHICH SAAS ARR $1.7M", on: true },
  { m: "M24", t: "Profitable at scale", s: "$25.9M run-rate", x: "OF WHICH SAAS ARR $5.4M" },
];

const S24 = () => (
  <Frame
    n={24}
    eyebrow="MILESTONES TO SERIES A · THE ASK"
    tagline={TAGLINE}
    title={<>$2.5M equity gets us<span className="lime"> to a Series A in 12 months.</span></>}
  >
    <div style={{ position: "absolute", left: 43.2, top: 178, width: 873.6 }}>
      <div
        className="gw"
        style={{ position: "absolute", left: 21.6, top: 48, height: 1, background: "rgba(255,255,255,.16)", "--w": "828px", "--d": "380ms" }}
      />
      <Row style={{ alignItems: "flex-start" }}>
        {MILES.map((m, i) => (
          <div key={m.m} className="an" style={{ width: 176, "--d": `${420 + i * 90}ms` }}>
            <Mono size={10} color={m.on ? LIME : "#6f8178"}>{m.m}</Mono>
            <div
              style={{
                width: 15, height: 15, borderRadius: 999, marginTop: 14,
                background: m.on ? LIME : OFFWHITE,
                boxShadow: m.on ? "0 0 0 4px rgba(220,255,58,.16)" : "none",
              }}
            />
            <div className="h" style={{ fontSize: 13, marginTop: 19, color: m.on ? LIME : "#fff" }}>{m.t}</div>
            <div style={{ fontSize: 10.5, color: "#9fb0a6", marginTop: 9, paddingRight: 14, lineHeight: "14px" }}>{m.s}</div>
            {m.x && <Mono size={8} color="#5c6e65" style={{ marginTop: 8 }}>{m.x}</Mono>}
          </div>
        ))}
      </Row>
    </div>

    <div className="vpd-card-a an" style={{ position: "absolute", left: 43.2, top: 330, width: 873.6, height: 140, padding: "22px 28px", display: "flex", "--d": "760ms" }}>
      <div style={{ width: 440 }}>
        <Mono size={9} color={LIME}>THE ASK</Mono>
        <div className="h" style={{ fontSize: 30, marginTop: 16 }}>$2.5M equity</div>
        <div className="h lime" style={{ fontSize: 24, marginTop: 10 }}>+ $2.5M reserve facility</div>
      </div>
      <div style={{ width: 1, background: "rgba(255,255,255,.12)", margin: "0 32px" }} />
      <div style={{ paddingTop: 8 }}>
        <div style={{ fontSize: 13, color: "#cfdcd4" }}>
          Post-money SAFE · <b style={{ color: "#fff" }}>$12.5M cap</b> <span className="grey">(proposed)</span>
        </div>
        <div style={{ fontSize: 13, color: "#cfdcd4", marginTop: 16 }}>
          EBITDA-positive <b style={{ color: "#fff" }}>Jun ’27</b>
        </div>
        <div style={{ fontSize: 11.5, color: "#8ea096", marginTop: 12 }}>
          Zev Ostreicher · zevost@gmail.com · verqo.com
        </div>
        <a
          href="/gtm"
          className="gtm-xlink an"
          style={{ marginTop: 10, "--d": "900ms" }}
          data-testid="pitch-gtm-link"
        >
          SEE THE FULL GTM <ArrowRight size={12} />
        </a>
      </div>
    </div>
  </Frame>
);

export const SLIDES = [S1, S2, S3, S4, S5, S6, S7, S8, S9, S10, S11, S12, S13, S14, S15, S16, S17, S18, S19, S20, S21, S22, S23, S24];
export const TOTAL = SLIDES.length;
